// src/data/store.js
// Firestore data layer with named exports used by your pages/components.

import { db } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";

const COLLECTION = "formulations";

/**
 * Simple list (legacy) — returns all documents (or limited by limitCount).
 */
export async function listFormulations(limitCount = null) {
  try {
    const q = limitCount
      ? query(collection(db, COLLECTION), orderBy("createdAt", "desc"), limit(limitCount))
      : query(collection(db, COLLECTION), orderBy("createdAt", "desc"));

    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const r = d.data();
      return {
        id: d.id,
        name: r.name ?? "",
        code: r.code ?? "",
        product: r.product ?? "",
        base: r.base ?? "",
        chart: r.chart ?? "",
        sizeL: Number(r.sizeL ?? 0),
        cans: Number(r.cans ?? 0),
        baseType: r.baseType ?? "",
        confidence: r.confidence ?? "",
        notes: r.notes ?? "",
        rows: Array.isArray(r.rows) ? r.rows : [],
        createdAt: r.createdAt ?? 0,
        updatedAt: r.updatedAt ?? r.createdAt ?? 0,
        creatorEmail: r.creatorEmail ?? "",
      };
    });
  } catch (err) {
    const msg = err?.message || err?.code || String(err);
    throw new Error(`Firestore read failed: ${msg}`);
  }
}

/**
 * Page-aware listing using Firestore cursors.
 *
 * Parameters:
 *  - startAfterDocSnapshot: a Firestore DocumentSnapshot after which to start (pass null to fetch first page)
 *  - pageSize: number of documents to return (default 20)
 *
 * Returns:
 *  {
 *    items: [ {id, ...fields}, ... ],
 *    lastDoc: DocumentSnapshot|null  // the last doc in the returned page (use as cursor for next page)
 *  }
 */
export async function listFormulationsPage(startAfterDocSnapshot = null, pageSize = 20) {
  try {
    const baseCollection = collection(db, COLLECTION);
    const q = startAfterDocSnapshot
      ? query(baseCollection, orderBy("createdAt", "desc"), startAfter(startAfterDocSnapshot), limit(pageSize))
      : query(baseCollection, orderBy("createdAt", "desc"), limit(pageSize));

    const snap = await getDocs(q);
    const items = snap.docs.map((d) => {
      const r = d.data();
      return {
        id: d.id,
        name: r.name ?? "",
        code: r.code ?? "",
        product: r.product ?? "",
        base: r.base ?? "",
        chart: r.chart ?? "",
        sizeL: Number(r.sizeL ?? 0),
        cans: Number(r.cans ?? 0),
        baseType: r.baseType ?? "",
        confidence: r.confidence ?? "",
        notes: r.notes ?? "",
        rows: Array.isArray(r.rows) ? r.rows : [],
        createdAt: r.createdAt ?? 0,
        updatedAt: r.updatedAt ?? r.createdAt ?? 0,
        creatorEmail: r.creatorEmail ?? "",
      };
    });

    const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { items, lastDoc };
  } catch (err) {
    const msg = err?.message || err?.code || String(err);
    throw new Error(`Firestore paged read failed: ${msg}`);
  }
}

/**
 * Create a new formulation.
 * Returns { id, ...data } (createdAt resolves on server; we return optimistic data).
 */
export async function createFormulation(data) {
  try {
    const payload = {
      ...data,
      createdAt: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, COLLECTION), payload);
    return { id: ref.id, ...data };
  } catch (err) {
    const msg = err?.message || err?.code || String(err);
    throw new Error(`Firestore write failed: ${msg}`);
  }
}

/**
 * Delete a formulation by Firestore doc id.
 */
export async function deleteFormulation(id) {
  try {
    if (!id) throw new Error("deleteFormulation: missing id");
    await deleteDoc(doc(db, COLLECTION, id));
  } catch (err) {
    const msg = err?.message || err?.code || String(err);
    throw new Error(`Firestore delete failed: ${msg}`);
  }
}
